/**
 * The paywall store — a framework-agnostic module singleton (same shape as the
 * upgrade-prompt store). A feature opens it with a typed reason; a host in the
 * billing feature subscribes and renders the paywall modal (plan checkout).
 * Keeping it outside React lets any feature trigger the paywall without
 * importing billing or crossing a feature boundary.
 *
 * The reason is a discriminated union so the paywall can grow new surfaces
 * without touching callers — e.g. a future `{ kind: "addon", ... }` that sells
 * credit packs or model add-ons instead of a plan upgrade.
 */
type PaywallPlan = "creator" | "pro_5k" | "ultra_13k" | "ultra_26k" | "ultra_40k";

export type PaywallReason = {
  /** The user picked a model their plan doesn't include. */
  kind: "model";
  /** Display name of the locked model, for the headline. */
  modelName: string;
  /** The cheapest plan that unlocks it — the one the CTA sells. */
  plan: PaywallPlan;
} | {
  /** A credit-consuming action requires a subscription. */
  kind: "credits";
  plan: PaywallPlan;
};

type Listener = () => void;

const listeners = new Set<Listener>();
let reason: PaywallReason | null = null;

function emit(): void {
  for (const listener of listeners) listener();
}

/** Open the paywall for a given reason (replaces any open one). */
export function openPaywall(next: PaywallReason): void {
  reason = next;
  emit();
}

export function closePaywall(): void {
  if (reason === null) return;
  reason = null;
  emit();
}

/** The current reason, or null when the paywall is closed. */
export function getPaywallReason(): PaywallReason | null {
  return reason;
}

export function subscribePaywall(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
